import { useState, useRef, useCallback, useEffect } from 'react';
import { fetchCoachingToken } from '../services/career-advisor.service';

/**
 * Career Advisor coaching session — adapted from the sales-demo `useVoiceSession`
 * engine. Same Azure OpenAI Realtime WebRTC mechanism: the browser connects
 * directly to Azure with a backend-minted ephemeral token. Differences from the
 * sales hook: the token comes from the interview-token coaching endpoint, the
 * session runs 10 minutes, and the prompt is the coaching prompt (set
 * server-side when the token is minted).
 */

export type AgentState = 'listening' | 'talking' | 'thinking' | null;
export type SessionStatus =
  | 'idle'
  | 'connecting'
  | 'active'
  | 'ending'
  | 'done'
  | 'error';

export interface ConversationTurn {
  role: 'user' | 'assistant';
  text: string;
  timestamp: number;
}

type CoachingSessionErrorKey =
  | 'career_advisor.stage.errors.microphone_permission'
  | 'career_advisor.stage.errors.not_available'
  | 'career_advisor.stage.errors.session_start_failed'
  | 'career_advisor.stage.errors.connection_failed'
  | 'career_advisor.stage.errors.guide_start_failed';

export interface CoachingSessionState {
  status: SessionStatus;
  secondsRemaining: number;
  transcript: ConversationTurn[];
  agentState: AgentState;
  error: CoachingSessionErrorKey | null;
}

// Short by design — long prep sessions stop being prep and become rehearsal.
// Backend also caps the number of sessions per candidate.
const SESSION_DURATION_SECONDS = 420;

// If neither side has spoken or moved for this long, treat the session as
// finished and tear it down. Belt-and-braces for cases where the AI omits the
// end_session tool call after its closing remarks, or the WebRTC stream goes
// quiet without firing a disconnect event. 60s leaves room for a candidate
// to genuinely think through a tough question without us cutting them off.
const INACTIVITY_TIMEOUT_MS = 60_000;
const INACTIVITY_CHECK_INTERVAL_MS = 5_000;

// WebRTC drops a connection into 'disconnected' on a brief network blip
// (Wi-Fi micro-cut, captive portal probe). We only treat it as a real death
// after this grace window — gives the ICE agent a chance to recover instead
// of nuking the session for a one-second hiccup.
const PEER_DISCONNECT_GRACE_MS = 4_000;

const INITIAL_STATE: CoachingSessionState = {
  status: 'idle',
  secondsRemaining: SESSION_DURATION_SECONDS,
  transcript: [],
  agentState: null,
  error: null,
};

export function useCoachingSession() {
  const [state, setState] = useState<CoachingSessionState>(INITIAL_STATE);

  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const dataChannelRef = useRef<RTCDataChannel | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const sessionStartRef = useRef<number>(0);
  const transcriptRef = useRef<ConversationTurn[]>([]);
  const inputAnalyserRef = useRef<AnalyserNode | null>(null);
  const outputAnalyserRef = useRef<AnalyserNode | null>(null);
  const animFrameRef = useRef<number | null>(null);
  const inputVolumeRef = useRef<number>(0);
  const outputVolumeRef = useRef<number>(0);
  const audioContextRef = useRef<AudioContext | null>(null);
  const audioElementRef = useRef<HTMLAudioElement | null>(null);
  const micStreamRef = useRef<MediaStream | null>(null);
  const languageRef = useRef<string>('en');
  const lastActivityRef = useRef<number>(0);
  const inactivityTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const endedRef = useRef<boolean>(false);
  const endingRef = useRef<boolean>(false);
  const responseDoneSinceEndingRef = useRef<boolean>(false);
  const agentTalkingRef = useRef<boolean>(false);
  const gracefulCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stopAnimationFrame = useCallback(() => {
    if (animFrameRef.current !== null) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
  }, []);

  const startVolumeLoop = useCallback(() => {
    const tick = () => {
      if (inputAnalyserRef.current) {
        const data = new Uint8Array(inputAnalyserRef.current.frequencyBinCount);
        inputAnalyserRef.current.getByteFrequencyData(data);
        const sum = data.reduce((a, b) => a + b, 0);
        inputVolumeRef.current = sum / data.length / 255;
      }
      if (outputAnalyserRef.current) {
        const data = new Uint8Array(outputAnalyserRef.current.frequencyBinCount);
        outputAnalyserRef.current.getByteFrequencyData(data);
        const sum = data.reduce((a, b) => a + b, 0);
        outputVolumeRef.current = sum / data.length / 255;
      }
      animFrameRef.current = requestAnimationFrame(tick);
    };
    animFrameRef.current = requestAnimationFrame(tick);
  }, []);

  const getInputVolume = useCallback((): number => inputVolumeRef.current, []);
  const getOutputVolume = useCallback((): number => outputVolumeRef.current, []);

  const appendTranscript = useCallback(
    (role: 'user' | 'assistant', text: string) => {
      if (!text?.trim()) return;
      const turn: ConversationTurn = {
        role,
        text: text.trim(),
        timestamp: Date.now(),
      };
      transcriptRef.current = [...transcriptRef.current, turn];
      lastActivityRef.current = Date.now();
      setState((prev) => ({ ...prev, transcript: transcriptRef.current }));
    },
    [],
  );

  const scheduleGracefulClose = useCallback(() => {
    if (gracefulCloseTimerRef.current) {
      clearTimeout(gracefulCloseTimerRef.current);
    }
    gracefulCloseTimerRef.current = setTimeout(() => {
      gracefulCloseTimerRef.current = null;
      endSessionRef.current?.();
    }, 300);
  }, []);

  const endSessionRef = useRef<(() => void) | null>(null);

  const handleDataChannelEvent = useCallback(
    (msg: Record<string, unknown>) => {
      // Any inbound event from Azure counts as "the session is alive" —
      // resets the inactivity watchdog.
      lastActivityRef.current = Date.now();
      switch (msg.type) {
        case 'input_audio_buffer.speech_started':
          setState((prev) => ({ ...prev, agentState: 'listening' }));
          break;
        case 'input_audio_buffer.speech_stopped':
          setState((prev) => ({ ...prev, agentState: 'thinking' }));
          break;
        case 'output_audio_buffer.started':
          agentTalkingRef.current = true;
          setState((prev) => ({ ...prev, agentState: 'talking' }));
          break;
        case 'output_audio_buffer.stopped':
          agentTalkingRef.current = false;
          setState((prev) => ({ ...prev, agentState: null }));

          if (endingRef.current && responseDoneSinceEndingRef.current) {
            scheduleGracefulClose();
          }
          break;
        case 'response.done':
          // Server says all output items in this response are done. The
          // client may still have audio in flight, though, so we DON'T close
          // here unconditionally — we close only when the audio buffer is
          // also silent.
          if (endingRef.current) {
            responseDoneSinceEndingRef.current = true;
            if (!agentTalkingRef.current) {
              scheduleGracefulClose();
            }
            // else: wait for the upcoming output_audio_buffer.stopped event.
          }
          break;
        case 'conversation.item.input_audio_transcription.completed':
          appendTranscript('user', (msg.transcript as string) ?? '');
          break;
        case 'response.output_audio_transcript.done':
          appendTranscript('assistant', (msg.transcript as string) ?? '');
          break;

        case 'response.function_call_arguments.done': {
          const fnName = msg.name as string;
          if (fnName === 'end_session' && !endingRef.current) {
            endingRef.current = true;
            // ----- Graceful-shutdown engineering -------------------------
            // Three concrete defences against the goodbye being clipped or
            // a rogue follow-up response being kicked off after the tool:
            //
            // (1) Mute the candidate's mic locally. WebRTC keeps the sender
            //     alive (so Azure doesn't tear the leg down) but transmits
            //     silence frames — Azure's VAD can no longer detect speech.
            const micTracks = micStreamRef.current?.getAudioTracks() ?? [];
            micTracks.forEach((t) => (t.enabled = false));
            // (2) Drop any candidate audio already buffered server-side.
            //     Mic mute alone isn't enough: anything Azure captured BEFORE
            //     we muted (a half-second of overlap, a late "thanks!") is
            //     still in `input_audio_buffer` and would trigger a new
            //     response once the current one closes. `input_audio_buffer
            //     .clear` drops it on the floor.
            if (dataChannelRef.current?.readyState === 'open') {
              dataChannelRef.current.send(
                JSON.stringify({ type: 'input_audio_buffer.clear' }),
              );
              // (3) Acknowledge the function call so the model treats the
              //     response as complete. We deliberately do NOT send a
              //     `response.create` after this — the response containing
              //     end_session runs to its natural `response.done` and we
              //     close there, in the handler above.
              dataChannelRef.current.send(
                JSON.stringify({
                  type: 'conversation.item.create',
                  item: {
                    type: 'function_call_output',
                    call_id: msg.call_id as string,
                    output: JSON.stringify({ success: true }),
                  },
                }),
              );
            }
            setState((prev) => ({ ...prev, status: 'ending' }));
            gracefulCloseTimerRef.current = setTimeout(() => {
              gracefulCloseTimerRef.current = null;
              endSessionRef.current?.();
            }, 10000);
          }
          break;
        }

        case 'session.created':
          if (dataChannelRef.current?.readyState === 'open') {
            // 1) Enable input transcription.
            dataChannelRef.current.send(
              JSON.stringify({
                type: 'session.update',
                session: {
                  type: 'realtime',
                  audio: {
                    input: {
                      transcription: {
                        model: 'gpt-4o-transcribe',
                        language: languageRef.current,
                      },
                    },
                  },
                },
              }),
            );
            // 2) Configure VAD + the end-session tool.
            //    Same VAD config as the sales demo (useVoiceSession.ts) —
            //    a high-threshold server_vad with long silence_duration_ms
            //    that's been battle-tested in production. The candidate can
            //    interrupt the coach with real speech, but the high threshold
            //    + 1.5s silence requirement filters out ambient noise.
            dataChannelRef.current.send(
              JSON.stringify({
                type: 'session.update',
                session: {
                  type: 'realtime',
                  audio: {
                    input: {
                      turn_detection: {
                        type: 'server_vad',
                        threshold: 0.9,
                        silence_duration_ms: 1500,
                        prefix_padding_ms: 300,
                      },
                    },
                  },
                  tools: [
                    {
                      type: 'function',
                      name: 'end_session',
                      description:
                        'CALL THIS to close the voice session. The session does not end on its own — you MUST call this tool immediately after your closing sentence whenever any of these are true: (a) you have wrapped up the prep, (b) the candidate says they are done / want to stop / have to go, (c) the conversation has naturally ended with nothing left to add. Do not stay silent waiting for the session to time out.',
                      parameters: {
                        type: 'object',
                        properties: {
                          reason: {
                            type: 'string',
                            description:
                              'Why the session is ending: "coaching_complete" or "candidate_requested"',
                          },
                        },
                      },
                    },
                  ],
                },
              }),
            );
            // 3) The advisor speaks first.
            dataChannelRef.current.send(JSON.stringify({ type: 'response.create' }));
          }
          break;
        case 'session.updated':
          break;
        case 'error': {
          const err = msg.error as Record<string, unknown> | undefined;
          console.error('[coaching] server error:', {
            code: err?.code,
            message: err?.message,
          });
          break;
        }
        default:
          break;
      }
    },
    [appendTranscript, scheduleGracefulClose],
  );

  const endSession = useCallback(() => {
    // Idempotent — end_session tool + ICE disconnect + inactivity watchdog
    // can all fire for the same session; only the first call should run.
    if (endedRef.current) return;
    endedRef.current = true;

    setState((prev) => ({ ...prev, status: 'ending' }));

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (inactivityTimerRef.current) {
      clearInterval(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
    if (gracefulCloseTimerRef.current) {
      clearTimeout(gracefulCloseTimerRef.current);
      gracefulCloseTimerRef.current = null;
    }
    stopAnimationFrame();
    inputVolumeRef.current = 0;
    outputVolumeRef.current = 0;

    if (dataChannelRef.current) {
      dataChannelRef.current.close();
      dataChannelRef.current = null;
    }
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((t) => t.stop());
      micStreamRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => undefined);
      audioContextRef.current = null;
    }
    if (audioElementRef.current) {
      audioElementRef.current.srcObject = null;
      audioElementRef.current = null;
    }
    inputAnalyserRef.current = null;
    outputAnalyserRef.current = null;

    setState((prev) => ({ ...prev, status: 'done', agentState: null }));
  }, [stopAnimationFrame]);

  // Hand `endSession` to `scheduleGracefulClose` through this ref so the
  // shutdown gates can fire the close without a circular useCallback dep.
  endSessionRef.current = endSession;

  const startSession = useCallback(
    async (invitationToken: string, language: string = 'en') => {
      setState({ ...INITIAL_STATE, status: 'connecting' });
      transcriptRef.current = [];
      languageRef.current = language;
      endedRef.current = false;
      endingRef.current = false;
      responseDoneSinceEndingRef.current = false;
      agentTalkingRef.current = false;
      lastActivityRef.current = Date.now();

      try {
        // Step 1: Fetch the ephemeral token from the coaching endpoint.
        const { token, webrtc_url } = await fetchCoachingToken(
          invitationToken,
          language,
        );

        // Step 2: Microphone access — all three browser-level processors
        // turned on so we hand Azure the cleanest possible signal: echo
        // cancellation kills room echo, noise suppression knocks down fans /
        // street noise, auto gain stabilises quiet candidates.
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            sampleRate: 24000,
          },
        });
        micStreamRef.current = stream;

        // Step 3: Peer connection.
        const pc = new RTCPeerConnection();
        peerConnectionRef.current = pc;
        stream.getTracks().forEach((track) => pc.addTrack(track, stream));

        // If Azure tears down the WebRTC leg (e.g. after end_session) or the
        // network drops for good, end the session locally — otherwise the UI
        // sits in 'active' forever even though the call is over. But 'failed'
        // and 'closed' are terminal; 'disconnected' is often a Wi-Fi blip
        // that recovers, so we give it a grace window before nuking.
        let disconnectGraceTimer: ReturnType<typeof setTimeout> | null = null;
        const clearDisconnectGrace = () => {
          if (disconnectGraceTimer) {
            clearTimeout(disconnectGraceTimer);
            disconnectGraceTimer = null;
          }
        };
        const onPeerDead = () => {
          if (endedRef.current) return;
          if (peerConnectionRef.current !== pc) return; // stale callback
          endSession();
        };
        const handlePeerState = (state: string) => {
          if (state === 'failed' || state === 'closed') {
            clearDisconnectGrace();
            onPeerDead();
            return;
          }
          if (state === 'disconnected') {
            if (disconnectGraceTimer) return; // already waiting
            disconnectGraceTimer = setTimeout(() => {
              disconnectGraceTimer = null;
              const ice = peerConnectionRef.current?.iceConnectionState;
              const conn = peerConnectionRef.current?.connectionState;
              // Still bad after the grace window? End it. Otherwise the
              // recovery transition (connected/completed) cleared the timer.
              if (
                ice === 'disconnected' ||
                ice === 'failed' ||
                ice === 'closed' ||
                conn === 'disconnected' ||
                conn === 'failed' ||
                conn === 'closed'
              ) {
                onPeerDead();
              }
            }, PEER_DISCONNECT_GRACE_MS);
            return;
          }
          // Recovered ('connected' / 'completed') — cancel pending teardown.
          if (state === 'connected' || state === 'completed') {
            clearDisconnectGrace();
          }
        };
        pc.oniceconnectionstatechange = () => handlePeerState(pc.iceConnectionState);
        pc.onconnectionstatechange = () => handlePeerState(pc.connectionState);

        // Step 4: Remote audio → speaker.
        const audioEl = new Audio();
        audioEl.autoplay = true;
        audioElementRef.current = audioEl;

        pc.ontrack = (event) => {
          audioEl.srcObject = event.streams[0];
          if (audioContextRef.current) {
            try {
              const outputSource = audioContextRef.current.createMediaStreamSource(
                event.streams[0],
              );
              const outputAnalyser = audioContextRef.current.createAnalyser();
              outputAnalyser.fftSize = 256;
              outputSource.connect(outputAnalyser);
              outputAnalyserRef.current = outputAnalyser;
            } catch {
              // output volume unavailable
            }
          }
        };

        // Step 5: Data channel BEFORE the SDP offer.
        const dc = pc.createDataChannel('realtime-channel');
        dataChannelRef.current = dc;

        dc.addEventListener('open', () => {
          sessionStartRef.current = Date.now();
          lastActivityRef.current = Date.now();
          setState((prev) => ({
            ...prev,
            status: 'active',
            secondsRemaining: SESSION_DURATION_SECONDS,
          }));
          timerRef.current = setInterval(() => {
            const elapsed = Math.floor(
              (Date.now() - sessionStartRef.current) / 1000,
            );
            const remaining = Math.max(0, SESSION_DURATION_SECONDS - elapsed);
            setState((prev) => ({ ...prev, secondsRemaining: remaining }));
            if (remaining === 0) endSession();
          }, 1000);
          // Inactivity watchdog — closes the session when nothing happens
          // for INACTIVITY_TIMEOUT_MS (no events, no transcripts). Belt-and-
          // braces for when the AI forgets to call end_session.
          inactivityTimerRef.current = setInterval(() => {
            if (endedRef.current) return;
            if (Date.now() - lastActivityRef.current > INACTIVITY_TIMEOUT_MS) {
              endSession();
            }
          }, INACTIVITY_CHECK_INTERVAL_MS);
        });

        dc.addEventListener('message', (event) => {
          try {
            const msg = JSON.parse(event.data as string) as Record<string, unknown>;
            handleDataChannelEvent(msg);
          } catch {
            // ignore malformed messages
          }
        });

        dc.addEventListener('close', () => {
          setState((prev) => ({ ...prev, agentState: null }));
        });

        // Step 6: SDP offer.
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);

        // Step 7: Exchange SDP with Azure.
        const sdpResponse = await fetch(webrtc_url, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/sdp',
          },
          body: offer.sdp,
        });
        if (!sdpResponse.ok) {
          throw new Error(`SDP exchange failed: ${sdpResponse.status}`);
        }
        const answerSdp = await sdpResponse.text();
        await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });

        // Step 8: Input volume analyser.
        const audioContext = new AudioContext();
        audioContextRef.current = audioContext;
        const inputSource = audioContext.createMediaStreamSource(stream);
        const inputAnalyser = audioContext.createAnalyser();
        inputAnalyser.fftSize = 256;
        inputSource.connect(inputAnalyser);
        inputAnalyserRef.current = inputAnalyser;

        startVolumeLoop();
      } catch (err) {
        const message = err instanceof Error ? err.message : '';
        let errorKey: CoachingSessionErrorKey;
        if (
          message.toLowerCase().includes('permission') ||
          message.toLowerCase().includes('notallowederror')
        ) {
          errorKey = 'career_advisor.stage.errors.microphone_permission';
        } else if (message.includes('not available') || message.includes('403')) {
          errorKey = 'career_advisor.stage.errors.not_available';
        } else if (message.includes('SDP exchange failed')) {
          errorKey = 'career_advisor.stage.errors.session_start_failed';
        } else if (
          message.includes('Failed to fetch') ||
          message.includes('NetworkError')
        ) {
          errorKey = 'career_advisor.stage.errors.connection_failed';
        } else {
          errorKey = 'career_advisor.stage.errors.guide_start_failed';
        }

        if (micStreamRef.current) {
          micStreamRef.current.getTracks().forEach((t) => t.stop());
          micStreamRef.current = null;
        }
        if (peerConnectionRef.current) {
          peerConnectionRef.current.close();
          peerConnectionRef.current = null;
        }
        stopAnimationFrame();

        setState((prev) => ({ ...prev, status: 'error', error: errorKey }));
      }
    },
    [endSession, handleDataChannelEvent, startVolumeLoop],
  );

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (inactivityTimerRef.current) clearInterval(inactivityTimerRef.current);
      if (gracefulCloseTimerRef.current) clearTimeout(gracefulCloseTimerRef.current);
      stopAnimationFrame();
      if (micStreamRef.current) {
        micStreamRef.current.getTracks().forEach((t) => t.stop());
      }
      if (dataChannelRef.current) dataChannelRef.current.close();
      if (peerConnectionRef.current) peerConnectionRef.current.close();
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => undefined);
      }
    };
  }, [stopAnimationFrame]);

  const resetSession = useCallback(() => {
    setState(INITIAL_STATE);
    transcriptRef.current = [];
  }, []);

  return {
    state,
    startSession,
    endSession,
    resetSession,
    getInputVolume,
    getOutputVolume,
  };
}
